import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from 'src/constants/queryKeys';
import { useGetAuthData } from 'src/hooks';
import { getUsersFilteredData, getUsersFilterInitial, putUserStatus } from 'src/api/usersManage';
import { IManageUser, IUsersManageInitial, IUsersManageStatuses } from 'src/typings/usersManage';
import { useCallback, useState } from 'react';
import { Hydra } from 'src/api/hydraResponse';

export const useUsersManage = () => {
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [getParams, setGetParams] = useState<string>('');
  const queryClient = useQueryClient();

  const {
    data: initialUsersData, isLoading: initialUsersDataIsLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.MANAGE_USERS_INITIAL],
    queryFn: async (): Promise<IUsersManageInitial | null> => {
      if (!jwt) return null;
      return (await getUsersFilterInitial(jwt)).data;
    },
    enabled: !!jwt,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });

  const {
    data: usersFilteredData, isLoading: filteredDataIsLoading, refetch: filteredDataRefetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.MANAGE_USERS, getParams],
    queryFn: async (): Promise<Hydra<IManageUser[]> | null> => {
      if (!jwt || !getParams.length) return null;
      return (await getUsersFilteredData(jwt, getParams)).data;
    },
    enabled: !!jwt && !!getParams.length,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
  });

  const changeComplexStatus = useCallback((userId: string, status: IUsersManageStatuses) => {
    return putUserStatus(jwt, userId, status);
  }, [jwt]);

  return {
    initialUsersDataIsLoading,
    filteredDataIsLoading,
    filteredDataRefetch,
    initialUsersData,
    usersFilteredData,
    getParams,
    queryClient,
    setGetParams,
    changeComplexStatus,
  };
};
